/**
 * LES TONS DU TEMPS QU'IL FAIT — la pastille de la tuile météo prend la couleur du ciel.
 *
 * POURQUOI ÇA EXISTE (décision utilisateur 2026-09-05 : « on peut pas adapter la couleur au temps ?
 * bleuté pour la neige, orange/jaune pour le temps ensoleillé, gris ou noir pour l'orage ou la
 * pluie »). La tuile portait deux états seulement : ambre par ciel dégagé, encre pour tout le reste.
 * Neige, orage, brouillard et grand vent se peignaient donc à l'identique, et la couleur n'apportait
 * rien à une tuile qu'on lit d'un coup d'œil entre deux chiffres.
 *
 * ⚠️ CE RENVERSEMENT EST ASSUMÉ, et il ne contredit PAS la doctrine « une couleur dit un SENS ». Le
 * commentaire d'origine refusait d'en faire une décoration ; ici la couleur encode la DONNÉE
 * elle-même — le temps qu'il fait — exactement comme un ton de jauge encode un palier. Ce qui reste
 * interdit est de peindre au hasard, pas de peindre ce que la mesure dit.
 *
 * ⚠️ AUCUNE COULEUR NOUVELLE N'EST INTRODUITE. Les quatre tons tirent des familles d'accent qui
 * existent déjà (`amber`, `ink`, `blue`, `violet`) : une cinquième famille inventée pour une seule
 * tuile aurait grossi la charte d'une teinte que rien d'autre n'emploie, et que la prochaine surface
 * aurait recopiée de travers.
 *
 * ⚠️ QUATRE TONS, PAS TREIZE. Un par ton et non un par temps : la pastille répond « beau / couvert /
 * mouillé / violent », et c'est l'ICÔNE qui distingue la bruine du grésil. Treize couleurs sur une
 * pastille de 52 px ne se mémorisent pas, donc ne signifient rien.
 */

/** Les quatre lectures possibles du ciel. */
export type WeatherTone = 'sun' | 'cloud' | 'rain' | 'storm';

/**
 * Chaque ton en trois jetons : le fond du dégradé, l'encre de l'icône, l'anneau.
 * ⚠️ Des NOMS de jetons, jamais des littéraux : le thème sombre redéfinit ces mêmes variables, et
 * une couleur écrite en dur ici serait juste en clair et fausse en sombre.
 */
export const WEATHER_TONES: Record<WeatherTone, { bg: string; fg: string; ring: string }> = {
  sun: { bg: 'amber-bg', fg: 'amber-fg', ring: 'amber' },
  // ⚠️ L'ANNEAU NEUTRE EST UN CRAN PLUS FRANC que le gris de bordure courant : au pas 200 il
  // disparaît à côté des trois tons colorés, et le ciel couvert se lit alors comme une pastille
  // INACHEVÉE plutôt que comme un gris choisi.
  cloud: { bg: 'ink-100', fg: 'ink-600', ring: 'ink-300' },
  rain: { bg: 'blue-bg', fg: 'blue-fg', ring: 'blue' },
  storm: { bg: 'violet-bg', fg: 'violet-fg', ring: 'violet' },
};

/**
 * Le temps → son ton. Liste FERMÉE et alignée sur les états que le service météo sait rendre.
 *
 * ⚠️ LA NEIGE EST RANGÉE AVEC LA PLUIE (bleu), et c'est voulu : ce sont deux formes de la même eau
 * qui tombe, et leurs icônes — gouttes contre flocon à six branches — les séparent déjà sans
 * ambiguïté. Leur donner deux bleus voisins aurait demandé une teinte de plus pour une différence
 * que personne ne voit sur une pastille.
 * ⚠️ LE VENT EST « COUVERT » et non « VIOLENT » : un grand vent n'est pas un orage, et le réserver au
 * ton violent aurait usé l'alarme sur le cas le plus courant de l'automne.
 */
export const WEATHER_KIND_TONES: Record<string, WeatherTone> = {
  clear: 'sun', fewClouds: 'sun',
  clouds: 'cloud', haze: 'cloud', fog: 'cloud', wind: 'cloud',
  drizzle: 'rain', rain: 'rain', freezingRain: 'rain', sleet: 'rain', snow: 'rain',
  storm: 'storm', tornado: 'storm',
};

/**
 * Le ton d'un temps donné — `cloud` quand on ne sait pas.
 * ⚠️ LE REPLI EST NEUTRE À DESSEIN : un temps inconnu peint en ambre annoncerait du soleil, un temps
 * inconnu peint en violet annoncerait un orage. Ne rien savoir se dit en gris.
 */
export function weatherTone(kind: string | null | undefined): WeatherTone {
  return (kind && WEATHER_KIND_TONES[kind]) || 'cloud';
}
